import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/utils/cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 rounded-lg border border-transparent font-semibold tracking-normal transition duration-150 disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--accent-solid))/0.18] focus-visible:ring-offset-2 focus-visible:ring-offset-white';

    const variants = {
      primary:
        'bg-[rgb(var(--accent-solid))] text-white shadow-sm shadow-slate-900/10 hover:bg-[rgb(var(--accent-solid))] hover:text-white hover:brightness-95 active:brightness-90',
      secondary:
        'border-slate-200 bg-white text-slate-900 shadow-sm shadow-slate-900/5 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 active:bg-slate-100',
      outline:
        'border-slate-300 bg-transparent text-slate-800 hover:border-slate-400 hover:bg-white hover:text-slate-950 active:bg-slate-100',
      ghost:
        'bg-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-950 active:bg-slate-200',
      danger:
        'bg-red-600 text-white shadow-sm shadow-red-900/10 hover:bg-red-700 hover:text-white active:bg-red-800',
    };

    const sizes = {
      sm: 'min-h-9 px-3 py-1.5 text-sm',
      md: 'min-h-11 px-5 py-2.5 text-sm',
      lg: 'min-h-12 px-6 py-3 text-base',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
